<script setup lang="ts" generic="P extends string | number">
import { computed, onMounted, ref } from 'vue';
import type {
  ErrorHandlerFunction,
  HandledResponse,
  HistoryDto,
  InitOverrideFunction,
} from '@/api/generator';
import HistoryProperty from './HistoryProperty.vue';
import Arrow from '@/components/history/HistoryArrow.vue';
import Headline from '@/components/history/HistorySectionHeadline.vue';
import type { I18nKey } from '@/utils/typings';
import { useTranslation } from 'i18next-vue';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import AvatarImage from '@/components/images/AvatarImage.vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';

const { t } = useTranslation();

interface HistoryEventsProps<T extends string | number> {
  id: T;
  historyApiFn: (
    requestParameters: { id: T },
    errorHandlers?: ErrorHandlerFunction[],
    initOverrides?: RequestInit | InitOverrideFunction,
  ) => Promise<HandledResponse<HistoryDto[]>>;
}

const props = defineProps<HistoryEventsProps<P>>();
const AVATAR_SIZE = 48;
const avatarSize = `${AVATAR_SIZE}px`;

const changes = ref<Array<HistoryDto>>([]);

const getHistory = async () => {
  const { result } = await props.historyApiFn({ id: props.id });
  if (result) {
    changes.value = result.slice().reverse() as Array<HistoryDto>;
  }
};

onMounted(getHistory);

const getChangeTitle = (item: HistoryDto) => {
  if (item.changeType === 'Created') {
    return t(`HistoryEvents.Added${item.entityType}` as I18nKey);
  } else if (item.changeType === 'Updated') {
    return t(`HistoryEvents.Modified${item.entityType}` as I18nKey);
  } else {
    return t(`HistoryEvents.Deleted${item.entityType}` as I18nKey);
  }
};

const getSummary = (item: HistoryDto): string =>
  item.summary?.map((i: string) => t(`HistoryEvents.${i}` as I18nKey)).join(', ') || '';
const isAddedHistoryItem = (item: HistoryDto, rootIndex: number) =>
  Boolean(item.propertyChangeSections?.length && rootIndex !== changes.value.length - 1);

const expanded = ref<number[]>([]);

function updateExpanded(value: unknown) {
  if (typeof value === 'number') {
    const isExpanded = expanded.value.includes(value);
    if (isExpanded) {
      expanded.value = expanded.value.filter((i) => i !== value);
    } else {
      expanded.value.push(value);
    }
  }
}
type DotColor = 'primary-lighten-3' | 'primary';

const dotColors = computed<Record<number, DotColor>>(() => {
  return changes.value.map((item, index) => {
    if (expanded.value.includes(index)) {
      return 'primary';
    }
    return 'primary-lighten-3';
  });
});
</script>

<template>
  <v-container justify="start">
    <v-timeline
      class="w-100"
      align="center"
      side="end"
      justify="auto"
      density="comfortable"
      hideOpposite
      truncateLine="both"
      fillDot
      data-testid="history_EventList"
    >
      <v-timeline-item
        v-for="(item, rootIndex) in changes"
        :key="item.changeTime"
        :dotColor="dotColors[rootIndex]"
        size="xx-small"
        width="100%"
      >
        <v-expansion-panels elevation="0" @update:modelValue="updateExpanded(rootIndex)">
          <v-expansion-panel :value="rootIndex" :focusable="!isAddedHistoryItem(item, rootIndex)">
            <v-expansion-panel-title
              :hideActions="!isAddedHistoryItem(item, rootIndex)"
              :class="{ 'actions-hidden': !isAddedHistoryItem(item, rootIndex) }"
            >
              <v-row class="pa-2">
                <v-col cols="auto" class="align-content-center">
                  <AvatarImage
                    v-if="item.user"
                    :tooltip="item.user.fullName"
                    :pictureId="item.user.profilePictureId"
                    :width="AVATAR_SIZE"
                  />
                  <div v-else class="avatar-placeholder"></div>
                </v-col>
                <v-col cols="4" class="align-content-center">
                  <IdentifierLabel
                    v-if="item.user"
                    :label="item.user?.fullName"
                    :identifier="item.user?.iuid"
                  />
                  <span v-else class="name text-body-2">PTW</span>
                </v-col>
                <v-col cols="3" class="align-content-center">
                  <DateTimeFormatter :value="item.changeTime" />
                </v-col>
                <v-col cols="2" class="align-content-center">
                  <v-chip>
                    {{ getChangeTitle(item) }}
                  </v-chip>
                </v-col>
                <v-col cols="2" class="align-content-center">
                  <span class="changes text-body-2 font-weight-medium text-truncate">{{
                    getSummary(item)
                  }}</span>
                </v-col>
              </v-row>
            </v-expansion-panel-title>
            <v-expansion-panel-text v-if="isAddedHistoryItem(item, rootIndex)">
              <div
                v-for="(section, index) in item.propertyChangeSections"
                :key="`${section.title} ${index}`"
                class="section"
              >
                <Headline
                  v-if="section.title"
                  :label="t(`HistoryEvents.${section.title}` as I18nKey)"
                  :hierarchy="3"
                />

                <div
                  v-for="(prop, innerIndex) in section.changesGroup"
                  :key="`${item.changeTime} ${innerIndex}`"
                  class="changes-group"
                >
                  <Headline
                    v-if="prop.type"
                    :label="t(`HistoryEvents.${prop.type}` as I18nKey)"
                    :hierarchy="4"
                  />

                  <div v-for="(change, innerIndex01) in prop.changes" :key="`$${innerIndex01}`">
                    <Headline
                      v-if="change.name"
                      :label="t(`HistoryEvents.${change.name}` as I18nKey)"
                      :hierarchy="5"
                    />

                    <div class="changes-row">
                      <HistoryProperty :isOldItem="true" :change="change" />
                      <Arrow />
                      <HistoryProperty :change="change" />
                    </div>
                  </div>
                </div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-timeline-item>
    </v-timeline>
  </v-container>
</template>

<style lang="scss" scoped>
.avatar-placeholder {
  width: v-bind(avatarSize);
  height: v-bind(avatarSize);
}
.section {
  margin-bottom: 20px;
  padding: 0 40px 10px 88px;
}

.title {
  color: rgb(var(--v-theme-primary-darken-2));
  margin-bottom: 4px;
}

.changes-group {
  margin-bottom: 20px;
}

.container-row {
  margin-bottom: 8px;
  display: flex;
}

.changes-row {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0;
}

.hr {
  height: 1px;
  background-color: rgb(var(--v-theme-primary-lighten-4));
  border: none;
  margin-bottom: 8px;
  margin-top: 8px;
}
.actions-hidden {
  // bring back the actions spacing of the hidden
  // collapse icon to keep everything aligned
  padding-right: calc(16px + 22.5px) !important;
}
</style>
